import { useEffect, useMemo, useState } from 'react';

import { IProgramSetup } from 'shared/interfaces/IProgramSetup';
import { Button } from 'shared/theme/elements';
import {
  ConfirmLine,
  ConfirmNote,
  ModalBody,
  ModalCard,
  ModalFoot,
  ModalIc,
  ModalTitle,
  ModalWrap,
  SchWarn,
  ScopeBox,
  ScopeRow,
} from './ProgramSetupSchedule.styled';
import {
  airingDaysOf,
  channelOf,
  ChangeScope,
  DAY_NAMES,
  fmtRange,
  nameOf,
  overlapPreview,
  ScheduleChange,
  ScheduleMode,
  slotOf,
} from './scheduleUtils';

export interface PendingChange {
  record: IProgramSetup;
  fromDay: number;
  next: ScheduleChange;
}

interface Props {
  mode: ScheduleMode;
  records: IProgramSetup[];
  pending: PendingChange | null;
  saving?: boolean;
  onCancel: () => void;
  onApply: (scope: ChangeScope) => void;
}

export const ScheduleChangeModal = ({ mode, records, pending, saving, onCancel, onApply }: Props) => {
  const [scope, setScope] = useState<ChangeScope>('one');

  useEffect(() => {
    setScope('one');
  }, [pending]);

  useEffect(() => {
    if (!pending) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [pending, saving, onCancel]);

  const preview = useMemo(
    () => (pending ? overlapPreview(records, pending.record, pending.fromDay, pending.next, scope) : null),
    [records, pending, scope],
  );

  if (!pending) return null;

  const { record, fromDay, next } = pending;
  const moved = next.day !== fromDay;
  const was = slotOf(record, fromDay);
  const days = airingDaysOf(record.daily_schedule);
  const name = nameOf(record, mode);

  let warning: string | null = null;
  if (preview && preview.added > 0 && preview.pairs.length) {
    const first = preview.pairs[0];
    const other = first.a._id === record._id ? first.b : first.a;
    const more = preview.pairs.length - 1;
    warning = `${name} would overlap ${nameOf(other, mode)} on ${DAY_NAMES[first.day]}${
      more > 0 ? ` and ${more} other day${more > 1 ? 's' : ''}` : ''
    }. Nothing else is changed or overwritten — apply only if the overlap is intended.`;
  }

  return (
    <ModalWrap className="ps-redesign" role="dialog" aria-modal="true" onClick={saving ? undefined : onCancel}>
      <ModalCard onClick={(event) => event.stopPropagation()}>
        <ModalIc>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3.5 2" />
          </svg>
        </ModalIc>
        <ModalTitle>Apply this schedule change?</ModalTitle>
        <ModalBody>
          {name} · {channelOf(record, mode)}
        </ModalBody>

        <ConfirmNote>
          <ConfirmLine>
            <span className="k">DAY</span>
            <span className="v ps-mono">
              {moved ? (
                <>
                  <span className="was">{DAY_NAMES[fromDay]}</span> → {DAY_NAMES[next.day]}
                </>
              ) : (
                DAY_NAMES[fromDay]
              )}
            </span>
          </ConfirmLine>
          <ConfirmLine>
            <span className="k">WAS</span>
            <span className="v was ps-mono">{was ? fmtRange(was) : '—'}</span>
          </ConfirmLine>
          <ConfirmLine>
            <span className="k">BECOMES</span>
            <span className="v ps-mono">{fmtRange(next)}</span>
          </ConfirmLine>
        </ConfirmNote>

        <ScopeBox>
          <div className="scopek">APPLY THIS CHANGE TO</div>
          <ScopeRow>
            <input type="radio" name="schScope" checked={scope === 'one'} onChange={() => setScope('one')} />
            <span>
              <b>This occurrence only</b>
              <small>
                {moved
                  ? `${DAY_NAMES[fromDay]} becomes Off schedule and ${DAY_NAMES[next.day]} takes this time.`
                  : `Changes ${DAY_NAMES[fromDay]} only and leaves the rest of the week as it is.`}
              </small>
            </span>
          </ScopeRow>
          <ScopeRow $off={moved}>
            <input
              type="radio"
              name="schScope"
              checked={scope === 'all'}
              disabled={moved}
              onChange={() => setScope('all')}
            />
            <span>
              <b>All occurrences of this program</b>
              <small>
                {moved
                  ? 'Not available for a day move — move the program one day at a time.'
                  : `Applies ${fmtRange(next)} to all ${days.length} day${
                      days.length === 1 ? '' : 's'
                    } this program airs (${days.map((d) => DAY_NAMES[d].slice(0, 3)).join(', ')}).`}
              </small>
            </span>
          </ScopeRow>
        </ScopeBox>

        {warning && (
          <SchWarn>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 7v6M12 16.5v.5" />
            </svg>
            <div>
              <b>This creates a schedule conflict.</b> {warning}
            </div>
          </SchWarn>
        )}

        <ModalFoot>
          <Button variant="outlined" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onApply(scope)} loading={saving}>
            Apply change
          </Button>
        </ModalFoot>
      </ModalCard>
    </ModalWrap>
  );
};
